import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CopyButton,
  EmptyHint,
  ListInput,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import {
  DEFAULT_CLASS_LIST,
  dedupe,
  parseList,
  shuffle,
} from "@/lib/random-engine";
import { ListOrdered, Printer, Shuffle, ArrowUpDown, Download, FileSpreadsheet, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export function LineOrder() {
  const [raw, setRaw] = usePersistentState("pickamini:line", DEFAULT_CLASS_LIST.join("\n"));
  const [soundOn, setSoundOn] = usePersistentState("pickamini:line:sound", true);
  const [order, setOrder] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const sfx = useSfx(soundOn);
  const names = dedupe(parseList(raw));

  const generateOrder = () => {
    if (names.length < 2) {
      setError("Add at least 2 names to generate a line order.");
      return;
    }
    sfx.click();
    setError(null);
    setOrder(shuffle(names));
    toast.success("Line order randomised!");
  };

  const shuffleNames = () => {
    sfx.click();
    setRaw(shuffle(names).join("\n"));
  };

  const sortNames = () => {
    sfx.click();
    setRaw([...names].sort().join("\n"));
  };

  const asText = order.length
    ? order.map((n, i) => `${i + 1}. ${n}`).join("\n")
    : "";

  const downloadCsv = () => {
    if (order.length === 0) return;
    let csv = "Position,Student Name\n";
    order.forEach((name, idx) => {
      csv += `"${idx + 1}","${name.replace(/"/g, '""')}"\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `line-order-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded CSV Line Order");
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
      {/* Settings Panel */}
      <Panel title="Roster Setup" className="no-print space-y-4">
        <div>
          <ListInput value={raw} onChange={setRaw} label="Student Names" count={names.length} />
          <div className="mt-2 flex gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={shuffleNames} disabled={names.length < 2}>
              <Shuffle className="size-3.5 mr-1" /> Shuffle
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={sortNames} disabled={names.length < 2}>
              <ArrowUpDown className="size-3.5 mr-1" /> Sort A-Z
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            size="lg"
            onClick={generateOrder}
            disabled={names.length < 2}
            className="w-full font-bold"
          >
            <Shuffle className="size-4 mr-2" />
            {order.length ? "Shuffle Line Again" : "Generate Line Order"}
          </Button>

          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <ResetButton onClick={() => setOrder([])} />
        </div>

        {error && (
          <div className="mt-3">
            <EmptyHint>{error}</EmptyHint>
          </div>
        )}
      </Panel>

      {/* Line Order Results & Print View */}
      <div>
        {order.length > 0 ? (
          <>
            {/* Toolbar for Copy, Print, Download */}
            <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <ListOrdered className="size-4 text-primary" />
                <span>
                  {order.length} Students in Order
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CopyButton text={asText} label="Copy Line Order" />

                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="size-3.5 mr-1.5" /> Print Line Order
                </Button>

                <Button variant="outline" size="sm" onClick={downloadCsv}>
                  <FileSpreadsheet className="size-3.5 mr-1.5" /> Download CSV
                </Button>
              </div>
            </div>

            {/* PRINT ONLY PROFESSIONAL HEADER */}
            <div className="hidden print:block mb-6 text-center border-b pb-4">
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                Class Line Order Roster
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Date: {new Date().toLocaleDateString()} • {order.length} Total Students
              </p>
            </div>

            {/* Main Order Cards Grid */}
            <div className="surface-card print-clean p-6 rounded-2xl bg-card border shadow-soft print:shadow-none print:border-slate-300">
              <ol className="grid gap-3 sm:grid-cols-2 print:grid-cols-2 print:gap-3">
                {order.map((name, i) => {
                  const isLeader = i === 0;
                  const isTail = i === order.length - 1;

                  return (
                    <li
                      key={name}
                      className={`animate-reveal flex items-center justify-between rounded-xl border p-3 transition-all ${
                        isLeader
                          ? "border-amber-400/80 bg-amber-500/10 dark:bg-amber-500/15 print:border-slate-400 print:bg-slate-50"
                          : isTail
                            ? "border-primary/40 bg-primary/5 print:border-slate-300 print:bg-slate-50"
                            : "border-border bg-secondary/30 print:border-slate-300 print:bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold ${
                            isLeader
                              ? "bg-amber-500 text-slate-950 font-black"
                              : "bg-primary text-primary-foreground"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className="font-display font-semibold text-foreground print:text-slate-900">
                          {name}
                        </span>
                      </div>

                      {/* Special role badges */}
                      {isLeader && (
                        <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-extrabold text-amber-600 dark:text-amber-400 uppercase tracking-wider print:border print:border-slate-400">
                          Line Leader
                        </span>
                      )}
                      {isTail && (
                        <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary uppercase tracking-wider print:border print:border-slate-400">
                          Door Holder
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        ) : (
          <div className="surface-card grid min-h-[300px] place-items-center p-8 text-center text-muted-foreground rounded-2xl">
            <div className="flex flex-col items-center gap-2 max-w-sm">
              <ListOrdered className="size-10 text-muted-foreground/60" />
              <p className="font-semibold text-foreground">No line order generated</p>
              <p className="text-xs text-muted-foreground">
                Add student names in the setup panel and click Generate Line Order to build your class queue.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
